import { Application, Container, Filter, GlProgram } from 'pixi.js';
import { isTouchDevice } from '../core/utils';
import { NpcManager } from './npc-manager';

const getDocumentHeight = (): number =>
  Math.max(document.body.scrollHeight, document.documentElement.scrollHeight);

const GLITCH_THRESHOLD = 120;
const GLITCH_DECAY = 0.92;
const GLITCH_MAX_OFFSET = 8;

const RGB_SPLIT_VERT = `
  in vec2 aPosition;
  out vec2 vTextureCoord;
  uniform vec4 uInputSize;
  uniform vec4 uOutputFrame;
  uniform vec4 uOutputTexture;

  vec4 filterVertexPosition(void) {
    vec2 position = aPosition * uOutputFrame.zw + uOutputFrame.xy;
    position.x = position.x * (2.0 / uOutputTexture.x) - 1.0;
    position.y = position.y * (2.0 * uOutputTexture.z / uOutputTexture.y) - uOutputTexture.z;
    return vec4(position, 0.0, 1.0);
  }

  vec2 filterTextureCoord(void) {
    return aPosition * (uOutputFrame.zw * uInputSize.zw);
  }

  void main(void) {
    gl_Position = filterVertexPosition();
    vTextureCoord = filterTextureCoord();
  }
`;

const RGB_SPLIT_FRAG = `
  in vec2 vTextureCoord;
  out vec4 finalColor;
  uniform sampler2D uTexture;
  uniform float uOffset;

  void main(void) {
    vec2 offset = vec2(uOffset, 0.0);
    float r = texture(uTexture, vTextureCoord + offset).r;
    vec4 original = texture(uTexture, vTextureCoord);
    float b = texture(uTexture, vTextureCoord - offset).b;
    finalColor = vec4(r, original.g, b, original.a);
  }
`;

export class NpcArena {
  private app: Application;
  private manager: NpcManager | null = null;
  private container: HTMLElement | null = null;
  private worldContainer: Container;
  private worldHeight = 0;
  private rgbSplitFilter: Filter | null = null;
  private glitchIntensity = 0;
  private lastScrollY = 0;
  private lastTime = 0;
  private scrollBound: (() => void) | null = null;

  constructor() {
    this.app = new Application();
    this.worldContainer = new Container();
  }

  async init(container: HTMLElement): Promise<void> {
    this.container = container;
    this.syncContainerSize();

    await this.app.init({
      backgroundAlpha: 0,
      resizeTo: container,
      antialias: false,
      roundPixels: true,
      resolution: Math.min(window.devicePixelRatio, 2),
    });

    const canvas = this.app.canvas as HTMLCanvasElement;
    canvas.classList.add('npc-arena-canvas');
    container.appendChild(canvas);

    if (!isTouchDevice()) {
      this.initGlitchFilter();
    }

    this.app.stage.addChild(this.worldContainer);

    this.worldHeight = getDocumentHeight();
    this.manager = new NpcManager(this.app, this.worldContainer, this.worldHeight);
    await this.manager.init();

    this.lastScrollY = window.scrollY;
    this.lastTime = performance.now();
    this.scrollBound = () => this.onScroll();
    window.addEventListener('scroll', this.scrollBound, { passive: true });
    window.addEventListener('resize', () => this.onResize());

    this.app.ticker.add(() => this.updateGlitch());
  }

  private initGlitchFilter(): void {
    const glProgram = GlProgram.from({
      vertex: RGB_SPLIT_VERT,
      fragment: RGB_SPLIT_FRAG,
    });

    this.rgbSplitFilter = new Filter({
      glProgram,
      resources: {
        uniforms: {
          uOffset: { value: 0, type: 'f32' },
        },
      },
    });

    this.app.stage.filters = [this.rgbSplitFilter];
  }

  private onScroll(): void {
    const now = performance.now();
    const currentScrollY = window.scrollY;
    const deltaTime = now - this.lastTime;

    if (deltaTime > 0) {
      const deltaScroll = Math.abs(currentScrollY - this.lastScrollY);
      const rawVelocity = (deltaScroll / deltaTime) * 1000;
      const target = rawVelocity > GLITCH_THRESHOLD
        ? Math.min((rawVelocity - GLITCH_THRESHOLD) / 400, 1)
        : 0;
      this.glitchIntensity = Math.max(this.glitchIntensity, target);
    }

    this.worldContainer.y = -currentScrollY;

    this.lastScrollY = currentScrollY;
    this.lastTime = now;
  }

  private updateGlitch(): void {
    this.glitchIntensity *= GLITCH_DECAY;
    if (this.glitchIntensity < 0.01) this.glitchIntensity = 0;

    if (this.rgbSplitFilter) {
      const offset = this.glitchIntensity * GLITCH_MAX_OFFSET / this.app.screen.width;
      this.rgbSplitFilter.resources.uniforms.uniforms.uOffset = offset;
    }
  }

  private syncContainerSize(): void {
    if (!this.container) return;
    this.container.style.position = 'fixed';
    this.container.style.top = '0';
    this.container.style.left = '0';
    this.container.style.width = '100%';
    this.container.style.height = `${window.innerHeight}px`;
  }

  private onResize(): void {
    this.syncContainerSize();
    this.worldHeight = getDocumentHeight();
    this.manager?.setWorldHeight(this.worldHeight);
    this.manager?.onResize();
  }

  dispose(): void {
    if (this.scrollBound) {
      window.removeEventListener('scroll', this.scrollBound);
    }
    this.manager?.dispose();
    this.app.destroy(true, { children: true });
  }
}
